import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconRefresh } from '@sorare/core/src/atoms/icons/system/iconRefresh.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';

export type LineupsStickyFooterAction = 'restart' | 'claim' | 'setLineup';

export const getLineupsStickyFooterAction = ({
  aasmState,
  canCompose,
}: {
  aasmState: ManagerProgressionTaskState;
  canCompose: boolean;
}): LineupsStickyFooterAction | null => {
  if (aasmState === ManagerProgressionTaskState.CANCELED) {
    return 'restart';
  }
  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    return 'claim';
  }
  if (aasmState === ManagerProgressionTaskState.READY && canCompose) {
    return 'setLineup';
  }
  return null;
};

type LineupsStickyFooterCallbacks = {
  onRestart?: () => void;
  onClaim: () => void;
  onSetLineup: () => void;
};

type LineupsStickyFooterPresentation = {
  icon?: IconDefinition;
  label: ReactNode;
  blueBackground?: boolean;
  onClick: () => void;
};

/** Shared icon / label / styling / handler for {@link LineupsStickyFooter} actions. */
export const getLineupsStickyFooterPresentation = (
  action: LineupsStickyFooterAction,
  { onRestart, onClaim, onSetLineup }: LineupsStickyFooterCallbacks
): LineupsStickyFooterPresentation | undefined =>
  ({
    ...(onRestart
      ? {
          restart: {
            icon: iconRefresh,
            label: <FormattedMessage defaultMessage="Restart" />,
            onClick: onRestart,
          },
        }
      : {}),
    claim: {
      icon: iconGift,
      label: <FormattedMessage defaultMessage="Claim prize" />,
      blueBackground: true,
      onClick: onClaim,
    },
    setLineup: {
      label: <FormattedMessage defaultMessage="Add team now" />,
      onClick: onSetLineup,
    },
  })[action];

type Props = LineupsStickyFooterCallbacks & {
  action: LineupsStickyFooterAction | null;
  banner?: ReactNode;
};

const StickyWrapper = styled(Vertical)`
  position: sticky;
  margin-top: auto;
  width: 100%;
  bottom: 0;
  max-width: 100%;
`;

const ButtonWrapper = styled(ButtonBase)`
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const AddButton = styled.div`
  position: relative;
  padding: var(--unit);
  margin-top: -30px;
  width: 120px;
  overflow: clip;
  display: flex;
  justify-content: center;

  &::before {
    --size: 30px;
    --m: 0.6;
    --p: calc(var(--m) * var(--size));
    --R: calc(var(--size) * sqrt(var(--m) * var(--m) + 1));
    position: absolute;
    display: block;
    z-index: -1;
    content: '';
    top: calc(-1 * var(--intermediate-unit));
    left: 50%;
    width: 240px;
    height: 120px;
    background: var(--background, var(--c-black));
    transform: translateX(-60px);
    mask:
      radial-gradient(
          var(--R) at 50% calc(var(--size) + var(--p)),
          #000 99%,
          #0000 101%
        )
        calc(50% - 2 * var(--size)) 0 / calc(4 * var(--size)) 100%,
      radial-gradient(var(--R) at 50% calc(-1 * var(--p)), #0000 99%, #000 101%)
        50% var(--size) / calc(4 * var(--size)) 100% repeat-x;
  }
`;

const Label = styled(LabelM)`
  background-color: var(--background, var(--c-black));
  width: 100%;
  padding: var(--triple-unit) var(--double-unit);
  margin-top: -30px;
`;

export const LineupsStickyFooter = ({
  action,
  banner,
  onRestart,
  onClaim,
  onSetLineup,
}: Props) => {
  const presentation = action
    ? getLineupsStickyFooterPresentation(action, {
        onRestart,
        onClaim,
        onSetLineup,
      })
    : undefined;

  if (!action) {
    return null;
  }

  return (
    <StickyWrapper center gap={5}>
      {banner}
      {!!presentation && (
        <ButtonWrapper
          style={
            presentation.blueBackground
              ? ({ '--background': 'var(--c-blue-400)' } as CSSProperties)
              : undefined
          }
          onClick={presentation.onClick}
        >
          <AddButton>
            <IconButton
              asSpan
              icon={presentation.icon}
              size="large"
              color={presentation.blueBackground ? 'tertiary' : undefined}
            />
          </AddButton>
          <Label bold>{presentation.label}</Label>
        </ButtonWrapper>
      )}
    </StickyWrapper>
  );
};
